import type { Team, TeamMember } from "@repo/shared-types";
import type { Read } from "./event-form";
import type { Person } from "./avatars";
import { newId } from "./ids";

/**
 * Teams as the web app handles them (0027): how a team's line names its
 * people, and reading the teammates box of the result form into what the api
 * takes when a team is formed.
 *
 * A team part's result is a team's: formed while recording, by whoever
 * records it, with members of the space invited by username and guests typed
 * by name. The api checks it all again.
 */

/** How many people a team holds, its creator, invitations and guests counted. */
export const MAX_TEAM = 20;

/** The longest guest name, as the api holds it. */
export const MAX_GUEST_NAME = 60;

/** A team's divisions, worked out from its people: each its own board. */
export const TEAM_DIVISIONS = [
  { value: "men", label: "Men" },
  { value: "women", label: "Women" },
  { value: "mixed", label: "Mixed" },
] as const;

export function teamDivisionLabel(division: string | null): string {
  return TEAM_DIVISIONS.find((option) => option.value === division)?.label ?? division ?? "";
}

/** A person on a team as a line names them: @username, or a guest's name. */
export function memberName(member: TeamMember): string {
  return member.user ? `@${member.user.username}` : (member.guestName ?? "");
}

/** A person on a team as their picture draws them: a guest has a name alone. */
export function memberAsPerson(member: TeamMember): Person {
  return member.user ?? { id: member.id, name: member.guestName, username: null, image: null };
}

/**
 * A team's line: the people it may name — its guests and those whose result
 * is shared, as the api sent them — then "and N others" for the rest, from
 * how many it has. "@ana, @bo, João and 1 other".
 */
export function teamNames(team: Pick<Team, "members" | "people">, only?: (member: TeamMember) => boolean): string {
  const named = team.members.filter((member) => (only ? only(member) : true)).map(memberName);
  const others = Math.max(0, team.people - named.length);
  if (others === 0) {
    return named.length <= 1 ? (named[0] ?? "") : `${named.slice(0, -1).join(", ")} and ${named.at(-1)}`;
  }
  const rest = others === 1 ? "1 other" : `${others} others`;
  return named.length === 0 ? `${team.people} people` : `${named.join(", ")} and ${rest}`;
}

/** What somebody is to a team, as a person reads it. */
export function roleLabel(role: string): string {
  switch (role) {
    case "creator":
      return "You look after its score";
    case "member":
      return "You're on it";
    case "invited":
      return "You're invited";
    default:
      return "";
  }
}

/** A team's page, under its event. */
export function teamPath(base: string, eventId: string, teamId: string): string {
  return `${base}/events/${eventId}/teams/${teamId}`;
}

/** The teams on one part. */
export function teamsOn(teams: Team[], partId: string): Team[] {
  return teams.filter((team) => team.partId === partId);
}

/** Whether the viewer is on a team, rather than invited to it. */
export function onTeam(team: Team): boolean {
  return team.role === "creator" || team.role === "member";
}

/**
 * In an event ranked overall, a team the viewer is on at a level (0037): it
 * holds their level for the event, which waits for them to leave it.
 */
export function teamHoldingLevel(teams: Team[]): Team | null {
  return teams.find((team) => onTeam(team) && team.level !== null) ?? null;
}

export type TeamPeople = {
  /** Members of the space, by username, without the @. */
  invite: string[];
  guests: { name: string; division: string }[];
};

/**
 * The teammates box of one part, named `${prefix}.team.invite` (one per
 * member, a username) and `${prefix}.team.guests.0.name` and `.division` and
 * so on — each person once, at least one, and no more than a team holds.
 */
export function readTeamPeople(form: FormData, prefix: string, label: string): Read<TeamPeople> {
  // Every message is about the one box the people are typed in.
  const field = `${prefix}.team`;
  const invite: string[] = [];
  for (const raw of form.getAll(`${prefix}.team.invite`)) {
    const username = String(raw).trim().replace(/^@/, "");
    if (username && !invite.includes(username)) invite.push(username);
  }
  const guests: TeamPeople["guests"] = [];
  for (let index = 0; form.has(`${prefix}.team.guests.${index}.name`); index++) {
    const name = String(form.get(`${prefix}.team.guests.${index}.name`) ?? "").trim();
    const division = String(form.get(`${prefix}.team.guests.${index}.division`) ?? "");
    if (!name) continue;
    if (name.length > MAX_GUEST_NAME) return { error: `${label}: a guest's name is at most ${MAX_GUEST_NAME} characters.`, field };
    if (division !== "men" && division !== "women") return { error: `${label}: is ${name} in men or women?`, field };
    guests.push({ name, division });
  }
  const people = invite.length + guests.length;
  if (people === 0) return { error: `${label}: add a teammate — a member by @username, or a guest by name.`, field };
  if (people + 1 > MAX_TEAM) return { error: `${label}: a team has ${MAX_TEAM} people at most, you included.`, field };
  return { value: { invite, guests } };
}

/** A new team's id, chosen here so a retry of the save cannot form it twice. */
export function newTeamId(): string {
  return newId();
}
